/**
 * Artigos do blog: delivery
 */

import type { BlogArticle } from '../types';

const PUBLISHED = '2026-09-27';

export const DELIVERY_ARTICLES: BlogArticle[] = [
  {
    slug: 'comissao-do-ifood-quanto-custa',
    title: 'Quanto custa a comissão do iFood e como calcular o impacto no seu lucro',
    description:
      'Entenda como funciona a comissão dos marketplaces de delivery, como calcular quanto ela pesa no seu lucro e quando vale ter um canal próprio.',
    excerpt:
      'A comissão parece pequena por pedido, mas no fim do mês vira uma conta grande. Veja como calcular e o que fazer para depender menos dela.',
    category: 'Delivery',
    keywords: [
      'comissão iFood',
      'quanto o iFood cobra',
      'taxa do iFood para restaurantes',
      'como vender sem iFood',
      'delivery sem comissão',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    blocks: [
      {
        type: 'p',
        text: 'Os marketplaces de delivery cobram um percentual sobre cada pedido. Os planos públicos das principais plataformas ficam, em geral, **entre 12% e 23% de comissão**, e o total pode **passar de 26%** quando se soma a taxa sobre pagamento online. Os valores mudam com frequência e variam por plano (entrega própria ou pela plataforma), então confira sempre as condições atuais no contrato.',
      },
      { type: 'h2', text: 'Como calcular quanto a comissão custa por mês' },
      {
        type: 'p',
        text: 'A conta é direta: **pedidos por mês × ticket médio × percentual total cobrado**.',
      },
      {
        type: 'table',
        head: ['Pedidos/mês', 'Ticket médio', 'Faturamento', 'Comissão de 20%'],
        rows: [
          ['300', 'R$ 50', 'R$ 15.000', 'R$ 3.000'],
          ['600', 'R$ 65', 'R$ 39.000', 'R$ 7.800'],
          ['1.000', 'R$ 70', 'R$ 70.000', 'R$ 14.000'],
        ],
      },
      {
        type: 'p',
        text: 'Agora compare com a sua **margem de lucro**. Se o lucro do restaurante é de 10% a 15% do faturamento, uma comissão de 20% pode ser maior do que tudo o que sobra no fim do mês. Faça a simulação com os seus números na [calculadora de economia](/#economia).',
      },
      { type: 'h2', text: 'O que entra na conta além da comissão' },
      {
        type: 'p',
        text: 'A comissão é o custo mais visível, mas não o único. Antes de calcular, confira no seu contrato e no extrato de repasses:',
      },
      {
        type: 'ul',
        items: [
          '**Taxa sobre pagamento online**, cobrada à parte quando o cliente paga pelo aplicativo.',
          '**Mensalidade do plano**, em alguns contratos, mesmo com poucas vendas.',
          '**Promoções e cupons** que a loja ajuda a pagar para aparecer em destaque.',
          '**Prazo de repasse**: o dinheiro das vendas pode levar dias ou semanas para cair, o que pesa no capital de giro.',
          '**Cancelamentos e estornos** que podem ser descontados do repasse.',
        ],
      },
      { type: 'h2', text: 'Passo a passo para calcular o seu custo real' },
      {
        type: 'ol',
        items: [
          'Some o **total de vendas** do mês no marketplace (o valor bruto dos pedidos).',
          'Some o **total repassado** para a sua conta no mesmo mês.',
          'A diferença entre os dois é o **custo real** do canal. Divida pelo total de vendas para ter o percentual.',
          'Compare esse percentual com a sua **margem de lucro**. Se o custo do canal é maior que a margem, cada pedido ali pode estar dando prejuízo.',
        ],
      },
      {
        type: 'tip',
        title: 'Exemplo',
        text: 'Vendas de R$ 39.000 no mês e repasse de R$ 30.420 significam R$ 8.580 de custo, ou **22% do faturamento**. Se a margem do restaurante é de 12%, o canal custa quase o dobro do que sobra.',
      },
      {
        type: 'p',
        text: 'Sinais de que o negócio depende demais do marketplace: mais da metade dos pedidos vem de lá, o caixa aperta esperando o repasse e qualquer mudança de regra ou de posição na lista derruba as vendas da semana.',
      },
      { type: 'h2', text: 'Então é melhor sair do marketplace?' },
      {
        type: 'p',
        text: 'Nem sempre. O marketplace traz **clientes novos** que ainda não conhecem o seu restaurante. O problema é depender só dele. A estratégia mais usada é:',
      },
      {
        type: 'ul',
        items: [
          'Usar o marketplace para **ser encontrado**.',
          'Levar o cliente que já comprou para o **seu canal próprio** (link, WhatsApp, QR Code), onde não há comissão.',
          'Dar um motivo para ele pedir direto: cupom, frete menor, pontos de fidelidade ou cashback.',
        ],
      },
      {
        type: 'tip',
        title: 'Cuidado',
        text: 'Leia as regras do seu contrato com o marketplace antes de divulgar o seu canal próprio dentro dos pedidos feitos pela plataforma.',
      },
      { type: 'h2', text: 'Quanto você economiza levando parte dos pedidos para o canal próprio' },
      {
        type: 'p',
        text: 'Não é preciso levar todos os clientes. Veja o efeito de migrar só uma parte, num exemplo com 600 pedidos por mês, ticket médio de R$ 65 e comissão total de 20%:',
      },
      {
        type: 'table',
        head: [
          'Pedidos no canal próprio',
          'Faturamento que deixa de pagar comissão',
          'Comissão evitada por mês',
        ],
        rows: [
          ['10% (60 pedidos)', 'R$ 3.900', 'R$ 780'],
          ['30% (180 pedidos)', 'R$ 11.700', 'R$ 2.340'],
          ['50% (300 pedidos)', 'R$ 19.500', 'R$ 3.900'],
        ],
      },
      {
        type: 'p',
        text: 'Desconte a mensalidade do sistema e as taxas do seu banco ou gateway no pagamento online. Mesmo assim, em geral sobra uma diferença grande, e ela cresce a cada cliente que passa a pedir direto.',
      },
      { type: 'h2', text: 'Como levar o cliente para o seu canal, em 5 passos' },
      {
        type: 'ol',
        items: [
          '**Tenha um link próprio** com o cardápio completo e o mesmo preço (ou menor) do que no aplicativo.',
          '**Dê um motivo para pedir direto**: cupom de primeira compra, frete menor, pontos de fidelidade ou cashback.',
          '**Esteja no WhatsApp** com resposta rápida: um robô atende no pico e já manda o link.',
          '**Divulgue onde o cliente já está**: Instagram, perfil do Google, embalagens e balcão (respeitando as regras do contrato com o marketplace).',
          '**Acompanhe todo mês** quantos pedidos vieram de cada canal e o custo de cada um.',
        ],
      },
      { type: 'h2', text: 'Quanto custa o canal próprio' },
      {
        type: 'p',
        text: 'Num sistema com mensalidade fixa, o custo não cresce quando você vende mais. No Vende Aí, por exemplo, os pedidos são ilimitados e não há comissão: você paga a mensalidade do plano e, no pagamento online, só a taxa do seu banco ou gateway. Veja mais em [delivery próprio x marketplace](/blog/delivery-proprio-x-marketplace).',
      },
      {
        type: 'cta',
        text: 'Simule sua economia e crie seu canal próprio no Vende Aí, com 30 dias grátis.',
      },
    ],
    faq: [
      {
        question: 'Quanto o iFood cobra de comissão?',
        answer:
          'Os planos públicos ficam, em geral, entre 12% e 23% de comissão, e o total pode passar de 26% somando a taxa sobre pagamento online. Confira sempre as condições atuais no contrato, porque os valores mudam.',
      },
      {
        question: 'Posso cobrar mais caro no marketplace do que no meu canal próprio?',
        answer:
          'Muitos restaurantes ajustam o preço no aplicativo para cobrir a comissão e mantêm o preço menor no canal próprio, como incentivo. Verifique as regras do seu contrato com a plataforma antes de fazer isso.',
      },
      {
        question: 'Como saber quanto o marketplace me custa de verdade?',
        answer:
          'Compare o total vendido no mês com o total repassado para a sua conta. A diferença, dividida pelas vendas, é o percentual real, já somando comissão, taxas e promoções.',
      },
      {
        question: 'Como vender delivery sem pagar comissão?',
        answer:
          'Com um canal próprio: loja online com link, pedidos pelo WhatsApp e QR Code na mesa, num sistema de mensalidade fixa. O pagamento online cai direto na sua conta, com apenas a taxa do banco ou gateway.',
      },
    ],
  },
  {
    slug: 'cardapio-digital-como-montar',
    title: 'Cardápio digital: como montar o seu em um dia (passo a passo)',
    description:
      'Passo a passo para montar um cardápio digital que vende: categorias, fotos, descrições, adicionais, preços por tamanho e como divulgar o link.',
    excerpt:
      'Cardápio digital bem feito vende mais e erra menos. Veja como organizar categorias, fotos, adicionais e divulgar o link no mesmo dia.',
    category: 'Delivery',
    keywords: [
      'cardápio digital',
      'como fazer cardápio digital',
      'cardápio digital para delivery',
      'cardápio online para restaurante',
      'cardápio com QR Code',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    blocks: [
      {
        type: 'p',
        text: 'Um **cardápio digital** é a vitrine do seu delivery: o cliente abre um link, vê os produtos com foto e preço, monta o pedido e paga. Diferente de um PDF ou de uma foto do cardápio impresso, ele é **interativo**, sempre atualizado e já entrega o pedido pronto para a cozinha.',
      },
      { type: 'h2', text: 'Passo a passo' },
      {
        type: 'ol',
        items: [
          '**Organize as categorias** na ordem em que o cliente pensa: destaques, pratos principais, porções, bebidas, sobremesas.',
          '**Escreva descrições que vendem**: ingredientes principais, tamanho e para quantas pessoas serve.',
          '**Use fotos reais**, bem iluminadas e com o mesmo padrão. Uma foto boa vale mais que um desconto.',
          '**Configure tamanhos e adicionais**: preço por tamanho, adicionais pagos, complementos obrigatórios (ex.: ponto da carne) e limites de escolha.',
          '**Defina horários, taxas de entrega e formas de pagamento.**',
          '**Teste como cliente**: faça um pedido do começo ao fim no celular.',
          '**Divulgue o link**: bio do Instagram, status do WhatsApp, Google e QR Code impresso na embalagem.',
        ],
      },
      { type: 'h2', text: 'Erros comuns' },
      {
        type: 'ul',
        items: [
          'Categorias demais: o cliente se perde. Prefira de 5 a 10.',
          'Adicional sem preço ou sem limite: vira prejuízo.',
          'Produto em falta aparecendo como disponível: marque como esgotado na hora.',
          'Cardápio no link diferente do cardápio no WhatsApp: use um só.',
        ],
      },
      {
        type: 'tip',
        title: 'Atalho',
        text: 'No Vende Aí, a loja já nasce com as categorias do seu segmento. Você pode cadastrar os produtos, importar por planilha ou trazer o catálogo do iFood.',
      },
      {
        type: 'p',
        text: 'Pizzaria? Veja como configurar [meio a meio e bordas](/blog/pizza-meio-a-meio-como-cobrar). Restaurante com salão? Veja [QR Code na mesa](/blog/qr-code-na-mesa-restaurante).',
      },
      {
        type: 'cta',
        text: 'Monte seu cardápio digital no Vende Aí hoje, com 30 dias grátis.',
      },
    ],
    faq: [
      {
        question: 'Cardápio digital precisa de aplicativo?',
        answer:
          'Não. O cardápio digital abre no navegador do celular por um link ou QR Code, sem o cliente baixar nada.',
      },
      {
        question: 'Qual a diferença entre cardápio digital e cardápio em PDF?',
        answer:
          'O PDF só mostra os produtos. O cardápio digital permite montar o pedido, escolher adicionais, pagar online e envia o pedido direto para o painel e a cozinha.',
      },
    ],
  },
  {
    slug: 'delivery-proprio-x-marketplace',
    title: 'Delivery próprio x marketplace: quando usar cada um (e por que usar os dois)',
    description:
      'Compare delivery próprio e marketplace em custo, clientes, dados e controle, e veja a estratégia de usar os dois canais sem depender de comissão.',
    excerpt:
      'Marketplace traz cliente novo, canal próprio traz lucro. Veja como combinar os dois do jeito certo.',
    category: 'Delivery',
    keywords: [
      'delivery próprio ou iFood',
      'delivery próprio x marketplace',
      'vale a pena ter delivery próprio',
      'alternativa ao iFood',
      'canal próprio de vendas',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    blocks: [
      {
        type: 'table',
        head: ['', 'Marketplace', 'Delivery próprio'],
        rows: [
          ['Custo', 'Comissão sobre cada pedido', 'Mensalidade fixa'],
          [
            'Clientes novos',
            'Traz clientes que ainda não te conhecem',
            'Depende da sua divulgação',
          ],
          ['Dados dos clientes', 'Ficam com a plataforma', 'São seus'],
          [
            'Fidelização',
            'Limitada às regras da plataforma',
            'Cupons, pontos, cashback e campanhas',
          ],
          ['Recebimento', 'Repasse em datas definidas', 'PIX e cartão direto na sua conta'],
          ['Concorrência na tela', 'Seu restaurante ao lado de outros', 'Só a sua loja'],
        ],
      },
      { type: 'h2', text: 'Quando o marketplace faz sentido' },
      {
        type: 'p',
        text: 'Para ser encontrado por quem ainda não conhece o restaurante, especialmente no começo ou em bairros novos.',
      },
      { type: 'h2', text: 'Quando o canal próprio faz sentido' },
      {
        type: 'p',
        text: 'Para **todo cliente que já comprou**. Quem já conhece o seu produto não precisa do marketplace para te achar: precisa de um link fácil, de um atendimento rápido no WhatsApp e de um motivo para voltar.',
      },
      { type: 'h2', text: 'Como migrar clientes para o canal próprio' },
      {
        type: 'ol',
        items: [
          'Tenha um link curto da loja e um QR Code impresso.',
          'Ofereça uma vantagem para pedir direto: cupom da primeira compra, frete menor ou pontos em dobro.',
          'Use o WhatsApp com robô para atender rápido quem chama direto.',
          'Crie um programa de fidelidade simples e campanhas para quem sumiu.',
        ],
      },
      {
        type: 'p',
        text: 'Quer ver o tamanho da diferença? Leia [quanto custa a comissão](/blog/comissao-do-ifood-quanto-custa).',
      },
      {
        type: 'cta',
        text: 'Crie seu canal próprio no Vende Aí e continue usando o marketplace se quiser.',
      },
    ],
    faq: [
      {
        question: 'Posso usar iFood e delivery próprio ao mesmo tempo?',
        answer:
          'Sim. A estratégia mais comum é usar o marketplace para ser encontrado e o canal próprio para os clientes que já compraram, sem comissão.',
      },
    ],
  },
  {
    slug: 'taxa-de-entrega-como-calcular',
    title: 'Taxa de entrega: como calcular por bairro sem perder dinheiro',
    description:
      'Como calcular a taxa de entrega do delivery por bairro ou distância, considerando custo do entregador, tempo, combustível e pedido mínimo.',
    excerpt:
      'Taxa de entrega errada ou afasta cliente ou come a margem. Veja um método simples para calcular por bairro.',
    category: 'Delivery',
    keywords: [
      'como calcular taxa de entrega',
      'taxa de entrega delivery',
      'taxa de entrega por bairro',
      'frete delivery restaurante',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    blocks: [
      {
        type: 'p',
        text: 'A taxa de entrega precisa cobrir o custo de levar o pedido sem assustar o cliente. O caminho mais simples é dividir a sua área de atendimento em **faixas ou bairros** e calcular o custo de cada uma.',
      },
      { type: 'h2', text: 'O que entra no custo da entrega' },
      {
        type: 'ul',
        items: [
          'Pagamento do entregador (por entrega, por hora ou diária).',
          'Combustível e manutenção, se a moto for sua.',
          'Tempo de ida e volta: quanto mais longe, menos entregas por hora.',
          'Embalagem térmica e perdas (produto que volta, pedido refeito).',
        ],
      },
      { type: 'h2', text: 'Método em 4 passos' },
      {
        type: 'ol',
        items: [
          'Calcule o **custo médio por entrega** na faixa mais próxima (ex.: até 3 km).',
          'Para cada faixa mais distante, some o tempo extra e o combustível.',
          'Defina um **pedido mínimo** por faixa para os bairros mais longes.',
          'Revise a cada três meses, ou quando o combustível ou o pagamento do entregador mudar.',
        ],
      },
      {
        type: 'tip',
        title: 'Frete como ferramenta de venda',
        text: 'Frete grátis acima de um valor mínimo aumenta o ticket médio. Use como recompensa de fidelidade ou cupom, não como regra para todo pedido.',
      },
      {
        type: 'p',
        text: 'No Vende Aí, você configura a taxa por região, atribui entregadores e acompanha o status de cada entrega no painel. Fidelidade e cupons podem dar frete grátis como recompensa.',
      },
      { type: 'cta', text: 'Configure suas taxas de entrega no Vende Aí, com 30 dias grátis.' },
    ],
    faq: [
      {
        question: 'É melhor cobrar taxa de entrega por bairro ou por distância?',
        answer:
          'Por bairro é mais fácil de entender para o cliente; por distância é mais preciso. Muitos restaurantes usam faixas de distância agrupadas por bairro.',
      },
    ],
  },
  {
    slug: 'carrinho-abandonado-delivery',
    title: 'Carrinho abandonado no delivery: como recuperar vendas perdidas',
    description:
      'Por que clientes abandonam o pedido no delivery e como recuperar essas vendas com mensagem no momento certo, cupom e checkout mais simples.',
    excerpt:
      'Muita gente monta o pedido e desiste no final. Veja por que isso acontece e como trazer esse cliente de volta.',
    category: 'Vendas',
    keywords: [
      'carrinho abandonado delivery',
      'recuperar vendas delivery',
      'cliente desistiu do pedido',
      'aumentar conversão cardápio digital',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    blocks: [
      {
        type: 'p',
        text: '**Carrinho abandonado** é quando o cliente escolhe os produtos, começa o pedido e não finaliza. Em qualquer loja online isso acontece com frequência, e no delivery os motivos costumam ser conhecidos.',
      },
      { type: 'h2', text: 'Motivos mais comuns' },
      {
        type: 'ul',
        items: [
          'Taxa de entrega que só aparece no fim.',
          'Forma de pagamento que ele queria não existe.',
          'Checkout longo, com cadastro obrigatório demais.',
          'Distração: o celular tocou, o cliente foi resolver outra coisa.',
        ],
      },
      { type: 'h2', text: 'Como reduzir' },
      {
        type: 'ol',
        items: [
          'Mostre a taxa de entrega cedo (ao informar o bairro ou CEP).',
          'Ofereça PIX, cartão online e pagamento na entrega.',
          'Peça só o necessário: nome, telefone e endereço.',
        ],
      },
      { type: 'h2', text: 'Como recuperar' },
      {
        type: 'p',
        text: 'Uma mensagem simples no WhatsApp, pouco tempo depois, resolve boa parte dos casos: "Vi que você começou um pedido. Posso ajudar a finalizar?". Um cupom pequeno ajuda quem estava em dúvida pelo preço.',
      },
      {
        type: 'p',
        text: 'No Vende Aí, a tela de carrinhos abandonados mostra quem desistiu e o que estava no carrinho, e as campanhas automáticas ajudam a trazer o cliente de volta.',
      },
      { type: 'cta', text: 'Recupere vendas perdidas com o Vende Aí. Teste grátis por 30 dias.' },
    ],
    faq: [
      {
        question: 'Como recuperar carrinho abandonado no delivery?',
        answer:
          'Identifique quem desistiu, envie uma mensagem pelo WhatsApp oferecendo ajuda para finalizar e, se fizer sentido, um cupom. Reduza também as causas: taxa de entrega escondida, poucas formas de pagamento e checkout longo.',
      },
    ],
  },
  {
    slug: 'como-abrir-um-delivery',
    title: 'Como abrir um delivery do zero: checklist completo',
    description:
      'Checklist para abrir um delivery: cardápio, precificação, embalagens, entrega, canais de venda, pagamento, divulgação e gestão desde o primeiro dia.',
    excerpt:
      'Do cardápio à primeira venda: o que você precisa organizar para abrir um delivery sem improviso.',
    category: 'Delivery',
    keywords: [
      'como abrir um delivery',
      'abrir delivery de comida',
      'como começar um delivery',
      'checklist delivery',
      'delivery em casa',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    blocks: [
      {
        type: 'p',
        text: 'Abrir um delivery é mais acessível do que abrir um restaurante com salão, mas exige organização. Use este checklist como roteiro.',
      },
      { type: 'h2', text: '1. Produto e preço' },
      {
        type: 'ul',
        items: [
          'Cardápio enxuto, com o que você faz bem e viaja bem.',
          'Ficha técnica de cada item para saber o custo real ([veja como](/blog/ficha-tecnica-e-cmv)).',
          'Preço que cubra custo, embalagem, taxa de pagamento e margem.',
        ],
      },
      { type: 'h2', text: '2. Formalização' },
      {
        type: 'ul',
        items: [
          'CNPJ adequado à atividade (consulte um contador sobre MEI ou outro regime).',
          'Licenças sanitárias e alvará exigidos pelo seu município.',
          'Emissão de nota fiscal quando exigida ([entenda NFC-e e NF-e](/blog/nota-fiscal-no-delivery)).',
        ],
      },
      { type: 'h2', text: '3. Operação' },
      {
        type: 'ul',
        items: [
          'Embalagens que mantêm temperatura e não vazam.',
          'Entrega própria, freelancers ou aplicativos de entrega.',
          'Área de atendimento e [taxa de entrega por região](/blog/taxa-de-entrega-como-calcular).',
        ],
      },
      { type: 'h2', text: '4. Canais de venda' },
      {
        type: 'ul',
        items: [
          'Cardápio digital com link próprio ([passo a passo](/blog/cardapio-digital-como-montar)).',
          'WhatsApp com atendimento automático ([robô de WhatsApp](/blog/robo-de-whatsapp-para-delivery)).',
          'Marketplace, se quiser alcance extra ([próprio x marketplace](/blog/delivery-proprio-x-marketplace)).',
        ],
      },
      { type: 'h2', text: '5. Pagamento e gestão' },
      {
        type: 'ul',
        items: [
          'PIX com confirmação automática e cartão online.',
          'Controle de estoque, contas a pagar e relatório de vendas desde o primeiro mês.',
        ],
      },
      { type: 'h2', text: '6. Divulgação' },
      {
        type: 'ul',
        items: [
          'Perfil no Google (Perfil da Empresa) com link da loja e horário.',
          'Instagram com fotos reais e o link na bio.',
          'Cupom de primeira compra e programa de fidelidade.',
        ],
      },
      {
        type: 'cta',
        text: 'Comece seu delivery com a loja pronta no Vende Aí: 30 dias grátis, sem cartão.',
      },
    ],
    faq: [
      {
        question: 'Preciso de CNPJ para abrir um delivery?',
        answer:
          'Para vender de forma regular e emitir nota fiscal, sim. O regime (MEI ou outro) depende do faturamento e da atividade; consulte um contador.',
      },
      {
        question: 'Qual o primeiro sistema que um delivery precisa?',
        answer:
          'Um cardápio digital com link próprio, que receba os pedidos num painel e aceite PIX e cartão. Com isso você já vende sem comissão e sem anotar pedido na mão.',
      },
    ],
  },
];
